/*
 * ns-gallery.js — Name Strings product media gallery
 * Native scroll-snap carousel (no library) + on-image dot indicator +
 * full-screen zoom viewer with pinch / double-tap / pan.
 * Exposes setActiveMedia(id, prepend) so theme variant switching keeps working.
 */
(function () {
  if (window.customElements && customElements.get('ns-gallery')) return;

  var RAF = window.requestAnimationFrame || function (cb) { return setTimeout(cb, 16); };

  function clamp(v, min, max) { return v < min ? min : (v > max ? max : v); }

  class NsGalleryEl extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.track = this.querySelector('.ns-gallery__track');
      this.slides = Array.prototype.slice.call(this.querySelectorAll('.ns-gallery__slide'));
      this.dotsWrap = this.querySelector('.ns-gallery__dots');
      this._buildDots();
      this.dots = Array.prototype.slice.call(this.querySelectorAll('.ns-gallery__dot'));
      this.prevBtn = this.querySelector('.ns-gallery__arrow--prev');
      this.nextBtn = this.querySelector('.ns-gallery__arrow--next');
      this.zoomBtn = this.querySelector('.ns-gallery__zoom');
      this.index = 0;
      if (!this.track || !this.slides.length) return;

      this._observeSlides();
      this._bindDots();
      this._bindArrows();
      this._bindZoom();

      // Set initial active slide to the pre-marked .is-active (variant featured)
      var activeIdx = this.slides.findIndex(function (s) { return s.classList.contains('is-active'); });
      if (activeIdx > 0) {
        // jump instantly without smooth scroll on load
        this._scrollToIndex(activeIdx, false);
      }
      this._setActive(activeIdx > -1 ? activeIdx : 0);
    }

    _buildDots() {
      if (!this.dotsWrap || this.dotsWrap.children.length) return;
      if (this.slides.length < 2) return;
      var frag = document.createDocumentFragment();
      for (var i = 0; i < this.slides.length; i++) {
        var b = document.createElement('button');
        b.type = 'button';
        b.tabIndex = -1; // dots live in an aria-hidden container; keep them out of the focus order
        b.className = 'ns-gallery__dot' + (i === 0 ? ' is-active' : '');
        b.setAttribute('aria-label', 'Go to image ' + (i + 1));
        frag.appendChild(b);
      }
      this.dotsWrap.appendChild(frag);
    }

    _observeSlides() {
      var self = this;
      if ('IntersectionObserver' in window) {
        this.io = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting && e.intersectionRatio >= 0.55) {
              var i = self.slides.indexOf(e.target);
              if (i > -1) self._setActive(i);
            }
          });
        }, { root: this.track, threshold: [0.55] });
        this.slides.forEach(function (s) { self.io.observe(s); });
      } else {
        // fallback: scroll listener
        this.track.addEventListener('scroll', function () {
          var i = Math.round(self.track.scrollLeft / self.track.clientWidth);
          self._setActive(clamp(i, 0, self.slides.length - 1));
        }, { passive: true });
      }
    }

    _setActive(i) {
      if (i === this.index && this._activeSet) return;
      this._activeSet = true;
      this.index = i;
      for (var d = 0; d < this.dots.length; d++) {
        this.dots[d].classList.toggle('is-active', d === i);
      }
      for (var s = 0; s < this.slides.length; s++) {
        this.slides[s].classList.toggle('is-active', s === i);
      }
      if (this.prevBtn) this.prevBtn.disabled = i <= 0;
      if (this.nextBtn) this.nextBtn.disabled = i >= this.slides.length - 1;
      // pause any playing video not on the active slide
      this._pauseInactiveMedia(i);
    }

    _pauseInactiveMedia(activeIdx) {
      for (var s = 0; s < this.slides.length; s++) {
        if (s === activeIdx) continue;
        var v = this.slides[s].querySelector('video');
        if (v && !v.paused) { try { v.pause(); } catch (e) {} }
      }
    }

    _scrollToIndex(i, smooth) {
      if (i < 0 || i >= this.slides.length) return;
      this.track.scrollTo({ left: this.track.clientWidth * i, behavior: smooth ? 'smooth' : 'auto' });
    }

    _bindDots() {
      var self = this;
      this.dots.forEach(function (dot, i) {
        dot.addEventListener('click', function () { self._scrollToIndex(i, true); self._setActive(i); });
      });
    }

    _bindArrows() {
      var self = this;
      if (this.prevBtn) this.prevBtn.addEventListener('click', function () { self._scrollToIndex(clamp(self.index - 1, 0, self.slides.length - 1), true); });
      if (this.nextBtn) this.nextBtn.addEventListener('click', function () { self._scrollToIndex(clamp(self.index + 1, 0, self.slides.length - 1), true); });
    }

    _bindZoom() {
      var self = this;
      if (this.zoomBtn) {
        this.zoomBtn.addEventListener('click', function (e) { e.preventDefault(); self.openZoom(self.index); });
      }
      // tap on image also opens zoom
      this.slides.forEach(function (s, i) {
        var img = s.querySelector('img');
        if (img && s.getAttribute('data-zoomable') === 'true') {
          img.addEventListener('click', function () { self.openZoom(i); });
        }
      });
    }

    // Called by theme variant switching: id = "{section}-{media.id}"
    setActiveMedia(mediaId) {
      var idx = -1;
      for (var i = 0; i < this.slides.length; i++) {
        if (this.slides[i].getAttribute('data-media-id') === mediaId) { idx = i; break; }
      }
      if (idx < 0) return;
      this._scrollToIndex(idx, false);
      this._setActive(idx);
    }

    /* ---------------- Full-screen zoom viewer ---------------- */
    openZoom(startIndex) {
      // Build the list of zoomable (image) slides only
      var zoomables = this.slides
        .map(function (s, i) { return { i: i, src: s.getAttribute('data-zoom-src'), alt: (s.querySelector('img') && s.querySelector('img').alt) || '' }; })
        .filter(function (z) { return z.src; });
      if (!zoomables.length) return;
      // map current index to position within zoomables
      var pos = 0;
      for (var z = 0; z < zoomables.length; z++) { if (zoomables[z].i <= startIndex) pos = z; }

      var viewer = new NsZoomViewer(zoomables, pos);
      viewer.open();
    }
  }

  /* ================= Zoom viewer ================= */
  function NsZoomViewer(items, startPos) {
    this.items = items;
    this.pos = startPos || 0;
  }
  NsZoomViewer.prototype.open = function () {
    var self = this;
    var root = document.createElement('div');
    root.className = 'ns-zoom';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');

    var slidesHtml = this.items.map(function (it) {
      return '<div class="ns-zoom__slide"><div class="ns-zoom__imgwrap"><img class="ns-zoom__img" src="' + it.src + '" alt="' + (it.alt ? it.alt.replace(/"/g, '&quot;') : '') + '" draggable="false"></div></div>';
    }).join('');

    root.innerHTML =
      '<button class="ns-zoom__close" type="button" aria-label="Close">' +
        '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"/></svg>' +
      '</button>' +
      '<div class="ns-zoom__track">' + slidesHtml + '</div>' +
      '<div class="ns-zoom__pager">' +
        '<button class="ns-zoom__nav ns-zoom__nav--prev" type="button" aria-label="Previous">' +
          '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M15 5l-7 7 7 7" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
        '</button>' +
        '<span class="ns-zoom__count"><span class="ns-zoom__cur">' + (this.pos + 1) + '</span> / ' + this.items.length + '</span>' +
        '<button class="ns-zoom__nav ns-zoom__nav--next" type="button" aria-label="Next">' +
          '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M9 5l7 7-7 7" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>' +
        '</button>' +
      '</div>';

    document.body.appendChild(root);
    document.documentElement.classList.add('ns-zoom-open');
    this.root = root;
    this.track = root.querySelector('.ns-zoom__track');
    this.slides = Array.prototype.slice.call(root.querySelectorAll('.ns-zoom__slide'));
    this.wraps = Array.prototype.slice.call(root.querySelectorAll('.ns-zoom__imgwrap'));
    this.curEl = root.querySelector('.ns-zoom__cur');
    this.navPrev = root.querySelector('.ns-zoom__nav--prev');
    this.navNext = root.querySelector('.ns-zoom__nav--next');

    // jump to starting slide
    this._scrollToPos(this.pos, false);
    this._transform = this.wraps.map(function () { return { scale: 1, x: 0, y: 0 }; });

    // close handlers
    root.querySelector('.ns-zoom__close').addEventListener('click', function () { self.close(); });
    this._onKey = function (e) { if (e.key === 'Escape') self.close(); else if (e.key === 'ArrowRight') self._go(1); else if (e.key === 'ArrowLeft') self._go(-1); };
    document.addEventListener('keydown', this._onKey);
    this.navPrev.addEventListener('click', function () { self._go(-1); });
    this.navNext.addEventListener('click', function () { self._go(1); });

    // active tracking on scroll
    this.track.addEventListener('scroll', function () {
      if (self._zoomed()) return;
      var p = Math.round(self.track.scrollLeft / self.track.clientWidth);
      if (p !== self.pos) { self.pos = clamp(p, 0, self.items.length - 1); self._updatePager(); }
    }, { passive: true });

    this._bindGestures();
    this._updatePager();
  };

  NsZoomViewer.prototype._scrollToPos = function (p, smooth) {
    this.track.scrollTo({ left: this.track.clientWidth * p, behavior: smooth ? 'smooth' : 'auto' });
  };
  NsZoomViewer.prototype._go = function (dir) {
    this._resetZoom(this.pos);
    this.pos = clamp(this.pos + dir, 0, this.items.length - 1);
    this._scrollToPos(this.pos, true);
    this._updatePager();
  };
  NsZoomViewer.prototype._updatePager = function () {
    if (this.curEl) this.curEl.textContent = this.pos + 1;
    if (this.navPrev) this.navPrev.disabled = this.pos <= 0;
    if (this.navNext) this.navNext.disabled = this.pos >= this.items.length - 1;
  };
  NsZoomViewer.prototype._zoomed = function () {
    var t = this._transform && this._transform[this.pos];
    return t && t.scale > 1.02;
  };
  NsZoomViewer.prototype._apply = function (i) {
    var t = this._transform[i];
    this.wraps[i].style.transform = 'translate3d(' + t.x + 'px,' + t.y + 'px,0) scale(' + t.scale + ')';
    this.track.style.overflowX = t.scale > 1.02 ? 'hidden' : 'auto';
    this.track.style.scrollSnapType = t.scale > 1.02 ? 'none' : 'x mandatory';
  };
  NsZoomViewer.prototype._resetZoom = function (i) {
    if (!this._transform[i]) return;
    this._transform[i] = { scale: 1, x: 0, y: 0 };
    this.wraps[i].style.transition = 'transform .25s ease';
    this._apply(i);
    var self = this;
    setTimeout(function () { if (self.wraps[i]) self.wraps[i].style.transition = ''; }, 260);
  };
  NsZoomViewer.prototype._clampPan = function (i) {
    var t = this._transform[i];
    var wrap = this.wraps[i];
    var rect = wrap.getBoundingClientRect();
    var vw = window.innerWidth, vh = window.innerHeight;
    // limit so image edges don't drift too far inside the viewport
    var maxX = Math.max(0, (rect.width - vw) / 2 + 40);
    var maxY = Math.max(0, (rect.height - vh) / 2 + 40);
    t.x = clamp(t.x, -maxX, maxX);
    t.y = clamp(t.y, -maxY, maxY);
  };

  NsZoomViewer.prototype._bindGestures = function () {
    var self = this;
    var pointers = {};
    var startDist = 0, startScale = 1, startMid = { x: 0, y: 0 }, startT = null;
    var panStart = null;
    var lastTap = 0;

    function activeWrap() { return self.wraps[self.pos]; }

    this.track.addEventListener('pointerdown', function (e) {
      pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      var ids = Object.keys(pointers);
      var t = self._transform[self.pos];
      if (ids.length === 2) {
        var a = pointers[ids[0]], b = pointers[ids[1]];
        startDist = Math.hypot(a.x - b.x, a.y - b.y);
        startScale = t.scale;
        startMid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        startT = { x: t.x, y: t.y };
      } else if (ids.length === 1 && t.scale > 1.02) {
        panStart = { x: e.clientX, y: e.clientY, tx: t.x, ty: t.y };
        try { self.track.setPointerCapture(e.pointerId); } catch (er) {}
      }
    });

    this.track.addEventListener('pointermove', function (e) {
      if (!pointers[e.pointerId]) return;
      pointers[e.pointerId] = { x: e.clientX, y: e.clientY };
      var ids = Object.keys(pointers);
      var t = self._transform[self.pos];
      if (ids.length === 2 && startDist) {
        var a = pointers[ids[0]], b = pointers[ids[1]];
        var dist = Math.hypot(a.x - b.x, a.y - b.y);
        t.scale = clamp(startScale * (dist / startDist), 1, 4);
        // keep pinch midpoint roughly stable
        t.x = startT.x;
        t.y = startT.y;
        self._clampPan(self.pos);
        self.wraps[self.pos].style.transition = '';
        self._apply(self.pos);
        e.preventDefault();
      } else if (ids.length === 1 && panStart && t.scale > 1.02) {
        t.x = panStart.tx + (e.clientX - panStart.x);
        t.y = panStart.ty + (e.clientY - panStart.y);
        self._clampPan(self.pos);
        self.wraps[self.pos].style.transition = '';
        self._apply(self.pos);
        e.preventDefault();
      }
    }, { passive: false });

    function up(e) {
      delete pointers[e.pointerId];
      var ids = Object.keys(pointers);
      var t = self._transform[self.pos];
      if (ids.length < 2) { startDist = 0; }
      if (ids.length === 0) {
        panStart = null;
        if (t.scale < 1.05) self._resetZoom(self.pos);
      }
    }
    this.track.addEventListener('pointerup', up);
    this.track.addEventListener('pointercancel', up);

    // double-tap to toggle zoom
    this.track.addEventListener('click', function (e) {
      var now = Date.now();
      if (now - lastTap < 300) {
        var t = self._transform[self.pos];
        var wrap = activeWrap();
        wrap.style.transition = 'transform .25s ease';
        if (t.scale > 1.02) {
          self._transform[self.pos] = { scale: 1, x: 0, y: 0 };
        } else {
          var rect = wrap.getBoundingClientRect();
          var cx = e.clientX - (rect.left + rect.width / 2);
          var cy = e.clientY - (rect.top + rect.height / 2);
          t.scale = 2.5;
          t.x = -cx * 1.5;
          t.y = -cy * 1.5;
          self._clampPan(self.pos);
        }
        self._apply(self.pos);
        setTimeout(function () { if (wrap) wrap.style.transition = ''; }, 260);
      }
      lastTap = now;
    });
  };

  NsZoomViewer.prototype.close = function () {
    document.removeEventListener('keydown', this._onKey);
    document.documentElement.classList.remove('ns-zoom-open');
    if (this.root && this.root.parentNode) this.root.parentNode.removeChild(this.root);
  };

  customElements.define('ns-gallery', NsGalleryEl);
})();
